import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useCreateDefect } from '../api/useDefects'
import { useProjects } from '@/features/projects/api/useProjects'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

// openapi: DefectCreate schema
interface DefectFormData {
  title: string
  test_project_id: number | null
  description: string
  severity: string
  priority: string
  defect_type: string
  steps_to_reproduce: string
  expected_behavior: string
  actual_behavior: string
  environment: string
  build_version: string
  browser: string
  os: string
  tags: string
}

const CreateDefectPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createDefect = useCreateDefect()
  const { data: projects } = useProjects()

  const [formData, setFormData] = useState<DefectFormData>({
    title: '',
    test_project_id: searchParams.get('project')
      ? parseInt(searchParams.get('project')!, 10)
      : null,
    description: '',
    severity: 'medium',       // openapi default
    priority: 'medium',       // openapi default
    defect_type: 'functional', // openapi default
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    environment: '',
    build_version: '',
    browser: '',
    os: '',
    tags: '',
  })

  const handleChange = (field: keyof DefectFormData, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // POST /api/defects
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required')
      return
    }
    if (!formData.test_project_id) {
      toast.error('Project is required')
      return
    }

    try {
      const payload: any = {
        title: formData.title.trim(),
        test_project_id: formData.test_project_id,
        severity: formData.severity,
        priority: formData.priority,
        defect_type: formData.defect_type,
      }

      // Only include optional fields if non-empty
      if (formData.description.trim())
        payload.description = formData.description.trim()
      if (formData.steps_to_reproduce.trim())
        payload.steps_to_reproduce = formData.steps_to_reproduce.trim()
      if (formData.expected_behavior.trim())
        payload.expected_behavior = formData.expected_behavior.trim()
      if (formData.actual_behavior.trim())
        payload.actual_behavior = formData.actual_behavior.trim()
      if (formData.environment.trim())
        payload.environment = formData.environment.trim()
      if (formData.build_version.trim())
        payload.build_version = formData.build_version.trim()
      if (formData.browser.trim())
        payload.browser = formData.browser.trim()
      if (formData.os.trim())
        payload.os = formData.os.trim()
      if (formData.tags.trim()) {
        payload.tags = formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      }

      const created = await createDefect.mutateAsync(payload)
      toast.success('Defect created')
      navigate(`/results/defects/${created.id}`)
    } catch {
      toast.error('Failed to create defect')
    }
  }

  const projectList = Array.isArray(projects)
    ? projects
    : (projects as any)?.projects || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/results/defects')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Defect</h1>
          <p className="text-muted-foreground">Report a new defect</p>
        </div>
      </div>

      {/* Defect Information */}
      <Card>
        <CardHeader>
          <CardTitle>Defect Information</CardTitle>
          <CardDescription>Provide details about the defect</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Project — required per openapi */}
          <div>
            <Label htmlFor="project">Project *</Label>
            <Select
              value={formData.test_project_id?.toString() || ''}
              onValueChange={(v) => handleChange('test_project_id', parseInt(v, 10))}
            >
              <SelectTrigger id="project">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projectList.map((p: any) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title — required per openapi (1-500 chars) */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Brief description of the defect"
              maxLength={500}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detailed description of the defect"
              rows={4}
            />
          </div>

          {/* Classification — openapi patterns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Severity: ^(critical|high|medium|low)$ */}
            <div>
              <Label>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(v) => handleChange('severity', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority: ^(critical|high|medium|low)$ */}
            <div>
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => handleChange('priority', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Defect Type: ^(functional|performance|security|ui|api)$ */}
            <div>
              <Label>Type</Label>
              <Select
                value={formData.defect_type}
                onValueChange={(v) => handleChange('defect_type', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="functional">Functional</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="ui">UI</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reproduction Details */}
      <Card>
        <CardHeader>
          <CardTitle>Reproduction Details</CardTitle>
          <CardDescription>Help developers reproduce and fix the issue</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="steps">Steps to Reproduce</Label>
            <Textarea
              id="steps"
              value={formData.steps_to_reproduce}
              onChange={(e) => handleChange('steps_to_reproduce', e.target.value)}
              placeholder="1. Navigate to...&#10;2. Click on...&#10;3. Observe..."
              rows={5}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="expected">Expected Behavior</Label>
              <Textarea
                id="expected"
                value={formData.expected_behavior}
                onChange={(e) => handleChange('expected_behavior', e.target.value)}
                placeholder="What should happen"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="actual">Actual Behavior</Label>
              <Textarea
                id="actual"
                value={formData.actual_behavior}
                onChange={(e) => handleChange('actual_behavior', e.target.value)}
                placeholder="What actually happened"
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Environment */}
      <Card>
        <CardHeader>
          <CardTitle>Environment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="environment">Environment</Label>
              <Input
                id="environment"
                value={formData.environment}
                onChange={(e) => handleChange('environment', e.target.value)}
                placeholder="e.g. staging, production"
              />
            </div>
            <div>
              <Label htmlFor="build_version">Build Version</Label>
              <Input
                id="build_version"
                value={formData.build_version}
                onChange={(e) => handleChange('build_version', e.target.value)}
                placeholder="e.g. v2.1.0"
              />
            </div>
            <div>
              <Label htmlFor="browser">Browser</Label>
              <Input
                id="browser"
                value={formData.browser}
                onChange={(e) => handleChange('browser', e.target.value)}
                placeholder="e.g. Chrome 121"
              />
            </div>
            <div>
              <Label htmlFor="os">Operating System</Label>
              <Input
                id="os"
                value={formData.os}
                onChange={(e) => handleChange('os', e.target.value)}
                placeholder="e.g. macOS 14.3"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tags */}
      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="e.g. regression, login, auth"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/results/defects')}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={createDefect.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {createDefect.isPending ? 'Creating...' : 'Create Defect'}
        </Button>
      </div>
    </div>
  )
}

export default CreateDefectPage
